import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { getAllLeave, getallSpeciality, getDoctorById, getDoctorSettings } from '../../redux/doctorSlice';
import { getDoctorPaymentSettings } from '../../redux/paymentSlice';
import DoctorProfileTab from '../../components/doctorTabs/DoctorProfileTab';
import SpecialitiesTab from '../../components/doctorTabs/SpecialitiesTab';
import AvailabilityTab from '../../components/doctorTabs/AvailabilityTab';
import LeaveTab from '../../components/doctorTabs/LeaveTab';
import AddressTab from '../../components/doctorTabs/AddressTab';
import PasswordTab from '../../components/doctorTabs/PasswordTab';
import DoctorPaymentTab from '../../components/doctorTabs/DoctorPaymentTab';
import { useTranslation } from 'react-i18next';
import CommonLoader from '../../components/CommonLoader';
import ErrorPage from '../../components/ErrorPage';
import { toast } from 'react-toastify';
import { tryingnavigate, setPendingNavigation, clearPendingNavigation } from '../../redux/blockingSlice';
import { ArrowLeftCircle } from 'lucide-react';
import DirectoryListingNote from '../../components/subscription/DirectoryListingNote';
import Medicines from '../Medicines';
import useTenantFeatures from '../../hooks/useTenantFeatures';

function DoctorProfile() {
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useDispatch();
    const isInitialCall = useRef(true);
    const { t, i18n } = useTranslation(['doctorprofile']);
    const { resnavigate, pendingNavigation } = useSelector((state) => state.blocking);

    const userRole = localStorage.getItem('Role');
    const doctorId = userRole === 'admin' ? location.state?.key : userRole === 'doctor' ? localStorage.getItem('ID') : userRole === 'patient' ? location.state?.key : '';
    const [activeTab, setActiveTab] = useState(() => location.state?.tab || 'profile');
    const { has: hasTenantFeature } = useTenantFeatures();
    const [mobileTabOpen, setMobileTabOpen] = useState(false);

    const { selectedDoctor, loading, error } = useSelector((state) => state.doctor);
    const { isLoading } = useSelector((state) => state.appointment);
    const doctorPaymentSettings = useSelector((state) => state.payment?.doctorSettings);

    console.log('selectedDoctor', selectedDoctor);
    useEffect(() => {
        if (doctorId && isInitialCall.current) {
            dispatch(getDoctorById(doctorId));

            if (userRole !== 'patient') {
                const year = new Date().getFullYear();
                dispatch(getDoctorSettings(doctorId));
                dispatch(getDoctorPaymentSettings(doctorId));
                dispatch(getAllLeave({ doctorId, year }));
            }

            isInitialCall.current = false;
        }
    }, [doctorId, dispatch]);

    // Close mobile dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (mobileTabOpen && !event.target.closest('[data-testid~="doctor-profile-mobile-tabs-dropdown"]')) {
                setMobileTabOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [mobileTabOpen]);

    const doctor = selectedDoctor || {
        name: '',
        email: '',
        phone: '',
        dob: '',
        gender: '',
        address: '',
    };

    // Check if profile is complete
    const profileComplete = selectedDoctor?.profileStatus === 'complete';

    const specialitiesFulfilled =
        (userRole === 'doctor' || userRole === 'admin')
            ? (
                Array.isArray(selectedDoctor?.appointmentSpeciality) &&
                selectedDoctor.appointmentSpeciality.length > 0 &&
                selectedDoctor.appointmentSpeciality.some(
                    s => Array.isArray(s.appointmentTypes) && s.appointmentTypes.length > 0
                )
            )
            : true;

    const addressesFulfilled = (userRole === 'doctor' || userRole === 'admin')
        ? selectedDoctor?.address?.length > 0
        : true; // No lock for non-doctor roles

    const availabilityFulfilled = (userRole === 'doctor' || userRole === 'admin')
        ? selectedDoctor?.availability?.some(avail => avail.active === true || avail.isActive === true) && selectedDoctor?.availability?.length > 0
        : true; // No lock for non-doctor roles

    // Payment & Bank is the 5th compulsory tab. It's "fulfilled" when the doctor
    // accepts at least one method and — if any non-cash (transaction) method is
    // accepted — has at least one bank account with a primary set. Cash-only
    // doctors need no bank account.
    const paymentFulfilled = (userRole === 'doctor' || userRole === 'admin')
        ? (() => {
            const accepted = doctorPaymentSettings?.acceptedPaymentMethods || [];
            if (accepted.length === 0) return false;
            const requiresBank = accepted.some((k) => k !== 'cash');
            if (!requiresBank) return true;
            const accts = doctorPaymentSettings?.bankAccounts || [];
            return accts.length > 0 && accts.some((a) => a.isPrimary);
        })()
        : true; // No lock for non-doctor roles

    const tabLocks = {
        specialities: !profileComplete,
        address: !specialitiesFulfilled,
        availability: !specialitiesFulfilled || !addressesFulfilled,
        payment: !availabilityFulfilled,
        leave: !availabilityFulfilled || !paymentFulfilled,
    };

    useEffect(() => {
        if (!loading && location.state?.tab) {
            const requestedTab = location.state.tab;
            if (!tabLocks[requestedTab]) {
                setActiveTab(requestedTab);
            }
        }
    }, [location.state?.tab, loading, selectedDoctor]);

    // After the dirty tab clears the block (Discard), auto-switch to the tab
    // the user originally clicked. We still respect tabLocks so a discarded
    // change can't slip past a precondition (e.g. unfinished profile).
    useEffect(() => {
        if (!resnavigate && pendingNavigation?.kind === 'tab' && pendingNavigation.value) {
            const targetTab = pendingNavigation.value;
            if (!tabLocks[targetTab]) {
                setActiveTab(targetTab);
            }
            dispatch(clearPendingNavigation());
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [resnavigate, pendingNavigation, dispatch]);

    const allTabs = [
        { key: 'profile', label: t('doctorprofile.tab1') },
        { key: 'specialities', label: t('doctorprofile.tab2') },
        // , role: 'doctor' 
        { key: 'address', label: t('doctorprofile.tab3') },
        { key: 'availability', label: t('doctorprofile.tab4') },
        { key: 'payment', label: t('doctorprofile.tab_payment', 'Payment & Bank') },
        { key: 'leave', label: t('doctorprofile.tab9') },
        // THE ADMIN MANAGES EACH DOCTOR'S MEDICINES (owner, 12 Oct 2026) — the
        // same list the doctor keeps on their own Medicines page, opened here
        // for this doctor. The doctor uses their page; the tab is the admin's.
        { key: 'medicines', label: t('doctorprofile.tab_medicines', 'Medicines'), role: 'admin', feature: 'medicines' },
        // Files and Keywords tabs retired (owner, 2026-09-02) — the toolbox and
        // advisory APIs behind them were deleted with the UI.
        { key: 'password', label: t('doctorprofile.tab7') }
    ];

    const visibleTabs = userRole === 'patient'
        ? allTabs.filter(tab => tab.key === 'profile')
        : allTabs.filter(tab => {
            // Hide password tab for admin role
            if (userRole === 'admin' && tab.key === 'password') {
                return false;
            }
            if (tab.feature && !hasTenantFeature(tab.feature)) return false;
            return !tab.role || tab.role === userRole;
        });

    if ((error?.status >= 500 && error?.status <= 599) || error?.message === 'Network Error') {
        return <ErrorPage error={error} pageName={t('doctorprofile.page_name', 'Doctor Profile')} />;
    }


    const handleTabClick = (e, key) => {
        if (loading) return;
        if (resnavigate) {
            e.preventDefault();
            // Remember the requested tab so it auto-applies once the dirty
            // tab's discard handler releases the block.
            dispatch(setPendingNavigation({ kind: 'tab', value: key }));
            dispatch(tryingnavigate(true)); // hit once only
            return;
        }

        if (tabLocks[key]) {
            let message = '';
            if (key === 'specialities') {
                message = t('doctorprofile.warning_profile') || 'You need to fulfill the profile first';
            } else if (key === 'address') {
                message = t('doctorprofile.warning_specialities') || 'You need to fulfill the specialities first';
            } else if (key === 'availability') {
                message = !specialitiesFulfilled
                    ? (t('doctorprofile.warning_specialities') || 'You need to fulfill the specialities first')
                    : (t('doctorprofile.warning_address') || 'You need to fulfill the address tab first');
            } else if (key === 'payment') {
                message = t('doctorprofile.warning_availability') || 'You need to set your availability schedule first';
            } else if (key === 'leave') {
                message = !availabilityFulfilled
                    ? (t('doctorprofile.warning_availability') || 'You need to set your availability schedule first')
                    : (t('doctorprofile.warning_payment') || 'You need to complete your payment & bank details first');
            }
            toast.warn(message);
        } else {
            setActiveTab(key);
        }
    };


    // The two admin links open pages that belong elsewhere in the menu; they
    // carry the way back to THIS doctor and tab, and keep Doctors lit in the
    // sidebar while you are there (owner, 20 Oct 2026; common/BackToOrigin).
    const openedFromProfile = () => {
        const name = [doctor.name, doctor.surname].filter(Boolean).join(' ').trim();
        return {
            navFrom: '/doctorProfile',
            backTo: {
                path: '/doctorProfile',
                state: { key: doctorId, tab: activeTab, returnTab: location.state?.returnTab },
                label: name
                    ? t('doctorprofile.back_to_doctor', 'Back to {{name}}', { name })
                    : t('doctorprofile.back_to_profile', 'Back to doctor profile'),
            },
        };
    };

    return (
        <div data-testid="doctor-profile-container" className="bg-white rounded-2xl p-4 shadow-xl border border-gray-100 sm:p-6 mx-auto">
            <div data-testid="doctor-profile-header" className="mb-8 flex gap-2">
                {userRole !== 'doctor' && (
                    <button
                        data-testid="doctor-profile-back-button"
                        onClick={() => navigate(userRole === 'patient' ? '/myDoctor' : '/doctor', { state: { returnTab: location.state?.returnTab } })}
                        disabled={loading}
                        className={`bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] text-white p-2 rounded-xl hover:from-[#3AA8A8] hover:to-[#2D8A8A] font-semibold transition-all duration-300 cursor-pointer shadow-lg hover:shadow-xl hover:scale-105 ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                        <ArrowLeftCircle data-testid="back-arrow-icon" className="h-5 w-5" />
                    </button>
                )}
                {userRole !== 'doctor' && (<h1 data-testid="doctor-profile-heading" className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] bg-clip-text text-transparent ml-2">{t('doctorprofile.heading')}</h1>)}
                {userRole === 'doctor' && (<h1 data-testid="doctor-profile-heading-my-profile" className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] bg-clip-text text-transparent ml-2">{t('doctorprofile.heading1')}</h1>)}
                {/* THE ADMIN'S TWO WAYS INTO THIS DOCTOR'S MONEY (owner, 16 Sep
                    2026). Both pages take the doctor from `?doctorId=`: the
                    ledger so the desk can mark a fee collected against the right
                    doctor, the subscription read-only so an admin can see which
                    plan a doctor is on without signing in as them. Shown only to
                    an admin looking at a doctor, never on a doctor's own profile
                    (they have these in the sidebar) and never to a patient. */}
                {userRole === 'admin' && doctorId && (
                    <div data-testid="doctor-profile-admin-links" className="ml-auto flex flex-wrap items-center gap-2">
                        <button
                            type="button"
                            data-testid="doctor-profile-payments-link"
                            onClick={() => navigate(`/payment?doctorId=${encodeURIComponent(doctorId)}`, { state: openedFromProfile() })}
                            className="px-4 py-2 rounded-xl text-sm font-semibold text-[#2D8A8A] bg-[#4ABDBD]/10 hover:bg-[#4ABDBD]/20 transition-colors cursor-pointer"
                        >
                            {t('doctorprofile.payments_ledger', 'Payments ledger')}
                        </button>
                        <button
                            type="button"
                            data-testid="doctor-profile-subscription-link"
                            onClick={() => navigate(`/mySubscription?doctorId=${encodeURIComponent(doctorId)}`, { state: openedFromProfile() })}
                            className="px-4 py-2 rounded-xl text-sm font-semibold text-[#2D8A8A] bg-[#4ABDBD]/10 hover:bg-[#4ABDBD]/20 transition-colors cursor-pointer"
                        >
                            {t('doctorprofile.subscription', 'Subscription')}
                        </button>
                    </div>
                )}
            </div>

            {/* 1 Oct 2026: the doctor's own profile says when their plan
                keeps them out of the platform directory (draws nothing
                anywhere else — see the component). */}
            {userRole === 'doctor' && <DirectoryListingNote className="-mt-5 mb-4" />}

            <div data-testid="doctor-profile-tabs-container" className="mb-4">
                {/* Desktop Navigation Tabs */}
                <div data-testid="doctor-profile-tabs" className="hidden w-fit sm:flex flex-wrap justify-start gap-2 bg-gradient-to-r from-gray-50 to-blue-50/30 p-1 rounded-2xl shadow border border-gray-200">
                    {visibleTabs.map(({ key, label }) => (
                        <button
                            key={key}
                            data-testid={`doctor-profile-tab-${key}`}
                            onClick={(e) => handleTabClick(e, key)}
                            className={`relative px-6 py-3 text-sm font-semibold transition-colors duration-300 rounded-xl z-10 ${activeTab === key && !loading && !tabLocks[key]
                                ? 'text-white shadow-md'
                                : 'text-gray-600 hover:text-[#4ABDBD] cursor-pointer'
                                } ${(loading || tabLocks[key]) ? 'opacity-50 cursor-not-allowed' : ''}`}
                        >
                            {activeTab === key && !loading && !tabLocks[key] && (
                                <motion.div
                                    layoutId="activeDoctorTab"
                                    className="absolute inset-0 bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] rounded-xl shadow-lg !m-0"
                                    transition={{ type: 'spring', bounce: 0.2, duration: 0.6 }}
                                />
                            )}
                            <span className="relative z-20">{label}</span>
                        </button>
                    ))}
                </div>

                {/* Mobile Navigation - Dropdown Style */}
                <div data-testid="doctor-profile-mobile-tabs-dropdown" className="sm:hidden">
                    <div className="bg-gradient-to-r from-gray-50 to-blue-50/30 rounded-2xl shadow-lg border border-gray-200 p-2">
                        <div className="relative">
                            <button
                                data-testid="mobile-tab-selector"
                                className="w-full flex items-center justify-between px-4 py-3 bg-white rounded-xl shadow-md hover:shadow-lg transition-all duration-300 text-gray-700 font-semibold cursor-pointer"
                                onClick={() => setMobileTabOpen(!mobileTabOpen)}
                            >
                                <span>{visibleTabs.find(tab => tab.key === activeTab)?.label}</span>
                                <svg
                                    className={`w-5 h-5 transition-transform duration-300 ${mobileTabOpen ? 'rotate-180' : ''}`}
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                </svg>
                            </button>

                            {mobileTabOpen && (
                                <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl shadow-xl border border-gray-200 z-50 overflow-hidden">
                                    {visibleTabs.map(({ key, label }) => (
                                        <button
                                            key={key}
                                            data-testid={`mobile-doctor-profile-tab-${key}`}
                                            onClick={(e) => {
                                                handleTabClick(e, key);
                                                setMobileTabOpen(false);
                                            }}
                                            className={`w-full text-left px-4 py-3 text-sm font-semibold transition-all duration-300 border-b border-gray-100 last:border-b-0
                                                ${activeTab === key && !loading && !tabLocks[key]
                                                    ? 'text-white bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A]'
                                                    : 'text-gray-600 hover:text-[#4ABDBD] hover:bg-gray-50'
                                                } ${(loading || tabLocks[key]) ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                                        >
                                            {label}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                <div data-testid="doctor-profile-content" className="p-4 pb-0">
                    {(loading || isLoading) ? (
                        <div data-testid="doctor-profile-loading" className="flex flex-col items-center justify-center py-4 pt-12">
                            <CommonLoader />
                            <span data-testid="doctor-profile-loading-text" className="text-xl text-[#4ABDBD]">{t('doctorprofile.loading')}</span>
                        </div>
                    ) : (
                        <>
                            {activeTab === 'profile' && <DoctorProfileTab doctor={doctor} />}
                            {activeTab === 'specialities' && <SpecialitiesTab />}
                            {activeTab === 'address' && <AddressTab />}
                            {activeTab === 'availability' && <AvailabilityTab />}
                            {activeTab === 'leave' && <LeaveTab />}
                            {activeTab === 'payment' && <DoctorPaymentTab doctor={doctor} />}
                            {activeTab === 'password' && <PasswordTab doctor={doctor} />}
                            {activeTab === 'medicines' && userRole === 'admin' && hasTenantFeature('medicines') && (
                                <Medicines doctorId={doctorId} embedded />
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}

export default DoctorProfile;